import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Actions as ApiCallActions } from '../../../redux/apiCall/reducers'
import DataTable from '../../../components/data-table'
import Modal from '../../../components/modal'
import general from '../../../utils/general'
import RoleBasedAccess from '../../../components/role-based-access'
import { UserTypeEnum } from '../../../utils/enums'
import useI18n from '../../../i18n/useI18n'

const defaultForm = {
  academicYear: new Date().getFullYear(),
  examType: '',
  minScore: '',
  maxScore: '',
  averageScore: '',
  lastYearMinScore: '',
  lastYearMaxScore: '',
  notes: ''
}

const AcceptedScoresModal = ({ isOpen, onClose, program }) => {
  const { t, language } = useI18n()
  const dispatch = useDispatch()
  const [scores, setScores] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingScoreId, setEditingScoreId] = useState(null)
  const [deletingScoreId, setDeletingScoreId] = useState(null)
  const [form, setForm] = useState(defaultForm)

  useEffect(() => {
    if (isOpen && program?.id) {
      fetchScores(program.id)
      setShowForm(false)
      setEditingScoreId(null)
      setForm(defaultForm)
    }
  }, [isOpen, program?.id])

  useEffect(() => {
    if (!isOpen) {
      setScores([])
      setDeletingScoreId(null)
      setEditingScoreId(null)
      setShowForm(false)
      setForm(defaultForm)
    }
  }, [isOpen])

  const fetchScores = (programId) => {
    setLoading(true)
    dispatch(
      ApiCallActions.Get({
        url: `programs/${programId}/accepted-scores`,
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const responseData = data?.data || data || []
          setScores(Array.isArray(responseData) ? responseData : [])
          setLoading(false)
        },
        onError: () => {
          setScores([])
          setLoading(false)
        }
      })
    )
  }

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  const resetForm = () => {
    setForm(defaultForm)
    setEditingScoreId(null)
  }

  const parseNumberField = (value, allowEmpty = true) => {
    if (value === '' && allowEmpty) return undefined
    const numeric = Number(value)
    if (Number.isNaN(numeric)) return null
    return numeric
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!program?.id || submitLoading) return

    const academicYear = parseNumberField(form.academicYear, false)
    if (!academicYear || academicYear < 2000 || academicYear > 2100) {
      general.notificationError(t('QUOTA_NOTIF_INVALID_YEAR'))
      return
    }

    const numericFields = ['minScore', 'maxScore', 'averageScore', 'lastYearMinScore', 'lastYearMaxScore']
    const payload = { academicYear }

    for (const field of numericFields) {
      const parsed = parseNumberField(form[field])
      if (parsed === null) {
        general.notificationError(t('SCORES_NOTIF_NUMERIC'))
        return
      }
      if (parsed !== undefined) {
        payload[field] = parsed
      }
    }

    if (payload.minScore !== undefined && payload.maxScore !== undefined && payload.minScore > payload.maxScore) {
      general.notificationError(t('SCORES_NOTIF_MIN_MAX_VALIDATION'))
      return
    }

    const examType = form.examType?.trim()
    if (examType) {
      payload.examType = examType
    }

    const notes = form.notes?.trim()
    if (notes) {
      payload.notes = notes
    }

    setSubmitLoading(true)

    const request = editingScoreId
      ? ApiCallActions.Put({
          url: `programs/${program.id}/accepted-scores/${editingScoreId}`,
          data: payload,
          showAlertOnError: true,
          showLoading: false,
          onSuccess: () => {
            general.messageSuccess(t('SCORES_MSG_UPDATED'))
            resetForm()
            setShowForm(false)
            fetchScores(program.id)
            setSubmitLoading(false)
          },
          onError: () => setSubmitLoading(false)
        })
      : ApiCallActions.Post({
          url: `programs/${program.id}/accepted-scores`,
          data: payload,
          showAlertOnError: true,
          showLoading: false,
          onSuccess: () => {
            general.messageSuccess(t('SCORES_MSG_CREATED'))
            resetForm()
            setShowForm(false)
            fetchScores(program.id)
            setSubmitLoading(false)
          },
          onError: () => setSubmitLoading(false)
        })

    dispatch(request)
  }

  const handleEdit = (row) => {
    setShowForm(true)
    setEditingScoreId(row.id)
    setForm({
      academicYear: row.academicYear ?? new Date().getFullYear(),
      examType: row.examType || '',
      minScore: row.minScore ?? '',
      maxScore: row.maxScore ?? '',
      averageScore: row.averageScore ?? '',
      lastYearMinScore: row.lastYearMinScore ?? '',
      lastYearMaxScore: row.lastYearMaxScore ?? '',
      notes: row.notes || ''
    })
  }

  const handleDelete = async (row) => {
    const programId = program?.id
    const scoreId = row?.id

    if (programId === undefined || programId === null || scoreId === undefined || scoreId === null) {
      general.notificationError(t('SCORES_NOTIF_NOT_FOUND'))
      return
    }
    if (deletingScoreId === scoreId) return

    const confirmed = await general.confirmDelete(t('SCORES_ENTITY_NAME'), `${row.academicYear || row.id}`)
    if (!confirmed) return

    setDeletingScoreId(scoreId)
    dispatch(
      ApiCallActions.Delete({
        url: `programs/${programId}/accepted-scores/${scoreId}`,
        showAlertOnError: true,
        showLoading: false,
        onSuccess: () => {
          general.showDeleteSuccess(t('SCORES_ENTITY_NAME'))
          fetchScores(programId)
          setDeletingScoreId(null)
        },
        onError: () => setDeletingScoreId(null)
      })
    )
  }

  const columns = useMemo(() => [
    {
      key: 'academicYear',
      label: t('QUOTA_COLUMNS_ACADEMIC_YEAR'),
      render: (value) => <span className="dt-badge dt-badge-primary">{value || '-'}</span>
    },
    {
      key: 'examType',
      label: t('SCORES_COLUMNS_EXAM_TYPE'),
      render: (value) => value || '-'
    },
    {
      key: 'minScore',
      label: t('SCORES_COLUMNS_MIN'),
      align: 'center',
      render: (value) => value ?? '-'
    },
    {
      key: 'maxScore',
      label: t('SCORES_COLUMNS_MAX'),
      align: 'center',
      render: (value) => value ?? '-'
    },
    {
      key: 'averageScore',
      label: t('SCORES_COLUMNS_AVERAGE'),
      align: 'center',
      render: (value) => value ?? '-'
    },
    {
      key: 'lastYearMinScore',
      label: t('SCORES_COLUMNS_LAST_YEAR_MIN'),
      align: 'center',
      render: (value) => value ?? '-'
    },
    {
      key: 'lastYearMaxScore',
      label: t('SCORES_COLUMNS_LAST_YEAR_MAX'),
      align: 'center',
      render: (value) => value ?? '-'
    },
    {
      key: 'notes',
      label: t('QUOTA_COLUMNS_NOTES'),
      render: (value) => value || '-'
    },
    {
      key: 'deletedAt',
      label: t('SCORES_COLUMNS_STATUS'),
      render: (value) => (value ? <span style={{ color: '#ef4444' }}>{t('SCORES_STATUS_DELETED')}</span> : <span style={{ color: '#16a34a' }}>{t('SCORES_STATUS_ACTIVE')}</span>)
    },
    {
      key: 'actions',
      label: t('QUOTA_COLUMNS_ACTIONS'),
      align: 'center',
      render: (_, row) => (
        <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
          <div className="d-flex justify-content-center" style={{ gap: '8px' }}>
            <button
              type="button"
              className="dt-btn dt-btn-secondary"
              onClick={() => handleEdit(row)}
            >
              <i className="zmdi zmdi-edit"></i>
            </button>
            <button
              type="button"
              className="dt-btn dt-btn-danger"
              onClick={() => handleDelete(row)}
              disabled={deletingScoreId === row.id}
            >
              <i className="zmdi zmdi-delete"></i>
            </button>
          </div>
        </RoleBasedAccess>
      )
    }
  ], [deletingScoreId, t, language])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('SCORES_MODAL_TITLE')}
      subtitle={general.getLocaleValue(program, 'name', language) || t('PROGRAMS_ENTITY').toLowerCase()}
      icon="zmdi-chart"
      size="xlarge"
    >
      <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
        <div className="d-flex justify-content-between align-items-center m-b-15">
          <div>
            <h6 className="m-b-0">{editingScoreId ? t('SCORES_FORM_EDIT_TITLE') : t('SCORES_FORM_NEW_TITLE')}</h6>
            <small className="text-muted">{t('SCORES_FORM_YEAR_RANGE')}</small>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-round btn-sm"
            onClick={() => {
              setShowForm((prev) => !prev)
              setEditingScoreId(null)
              resetForm()
            }}
          >
            <i className="zmdi zmdi-plus m-r-5"></i>
            {showForm ? t('SCORES_FORM_HIDE') : t('SCORES_FORM_ADD')}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="card p-3 m-b-20">
            <div className="row clearfix">
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('QUOTA_COLUMNS_ACADEMIC_YEAR')}</label>
                  <input
                    type="number"
                    className="form-control"
                    min="2000"
                    max="2100"
                    value={form.academicYear}
                    onChange={(e) => handleChange('academicYear', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('SCORES_COLUMNS_EXAM_TYPE')}</label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength="50"
                    value={form.examType}
                    onChange={(e) => handleChange('examType', e.target.value)}
                    placeholder={t('SCORES_FORM_EXAM_PLACEHOLDER')}
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('SCORES_COLUMNS_MIN')}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.minScore}
                    onChange={(e) => handleChange('minScore', e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('SCORES_COLUMNS_MAX')}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.maxScore}
                    onChange={(e) => handleChange('maxScore', e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>
            </div>
            <div className="row clearfix">
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('SCORES_COLUMNS_AVERAGE')}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.averageScore}
                    onChange={(e) => handleChange('averageScore', e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('SCORES_COLUMNS_LAST_YEAR_MIN')}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.lastYearMinScore}
                    onChange={(e) => handleChange('lastYearMinScore', e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('SCORES_COLUMNS_LAST_YEAR_MAX')}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.lastYearMaxScore}
                    onChange={(e) => handleChange('lastYearMaxScore', e.target.value)}
                    step="0.01"
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <label>{t('QUOTA_COLUMNS_NOTES')}</label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength="150"
                    value={form.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder={t('SCORES_FORM_NOTES_PLACEHOLDER')}
                  />
                </div>
              </div>
            </div>
            <div className="text-right">
              <button
                type="button"
                className="btn btn-default btn-round m-r-10"
                onClick={resetForm}
                disabled={submitLoading}
              >
                {t('QUOTA_FORM_CLEAR')}
              </button>
              <button
                type="submit"
                className="btn btn-success btn-round"
                disabled={submitLoading}
              >
                {submitLoading ? t('QUOTA_FORM_SAVING') : editingScoreId ? t('SCORES_FORM_EDIT_TITLE') : t('QUOTA_FORM_ADD')}
              </button>
            </div>
          </form>
        )}
      </RoleBasedAccess>

      <DataTable
        columns={columns}
        data={scores}
        loading={loading}
        emptyMessage={t('SCORES_EMPTY_MESSAGE')}
        emptyIcon="zmdi-chart"
        rowKey="id"
        itemLabel={t('QUOTA_ITEM_LABEL')}
        pagination={{
          enabled: true,
          defaultItemsPerPage: 5,
          itemsPerPageOptions: [5, 10, 25],
          showInfo: true
        }}
        hoverable={true}
      />
    </Modal>
  )
}

export default AcceptedScoresModal
